import React, { useState, useEffect, useRef } from 'react';
import { 
  Zap, 
  X, 
  Check, 
  AlertCircle, 
  Loader2, 
  ArrowRight, 
  CornerDownLeft, 
  Clock, 
  ShieldCheck,
  Paperclip,
  Camera,
  FileText,
  Image as ImageIcon,
  Tag,
  Users,
  Calendar,
  Package,
  DollarSign,
  Compass,
  ArrowUpRight,
  Mic,
  MicOff
} from 'lucide-react';
import { 
  sendAgenticCommand, 
  confirmAgenticMutation, 
  processAgenticDocumentFile,
  prepareFinancialDraft,
  looksLikeFinancialIntent,
  prepareServicesDraft,
  looksLikeServicesIntent,
  prepareClientDraft,
  looksLikeClientIntent,
  prepareInventoryDraft,
  looksLikeInventoryIntent,
  prepareAppointmentDraft,
  looksLikeAppointmentIntent,
  prepareWorkOrderDraft,
  looksLikeWorkOrderIntent,
  prepareRecipeDraft,
  looksLikeRecipeIntent,
  AgenticCommandResponse 
} from '../services/agenticCommandService';
import { supabase } from '../../../lib/supabase';
import {
  BRIDGE_EVENTS,
  getBridgeStorageKey,
  stageFinancialDraft,
  stageServiceDrafts,
  stageClientDraft,
  stageInventoryDraft,
  stageAppointmentDraft,
  stageWorkOrderDraft,
  stageRecipeDraft,
  BRIDGE_DESTINATIONS
} from '../utils/commandBridge';

interface AgenticCommandModalProps {
  isOpen: boolean;
  onClose: () => void;
  userTier?: 'free' | 'pro' | 'especialista' | 'empresa';
  onRefreshData?: () => void;
  onNavigate?: (view: any) => void;
  /** Identificador da tela de Centro de Custos no roteador de quem chama (padrão: 'custos'). */
  costsViewId?: string;
  /** Identificador da tela Contas a Pagar & Receber (padrão: 'financial_hub'). */
  financialViewId?: string;
  /** Identificador da tela de CRM de Clientes (padrão: 'clients'). */
  clientsViewId?: string;
  /** Identificador da tela de Estoque e Lotes (padrão: 'estoque_hub'). */
  inventoryViewId?: string;
  /** Identificador da tela de Agenda (padrão: 'calendar'). */
  appointmentsViewId?: string;
  /** Identificador da tela de Ordens de Serviço (padrão: 'work-orders'). */
  workOrdersViewId?: string;
  /** Chamado quando o recurso pedido exige plano superior (abre o aviso de upgrade). */
  onRequestUpgrade?: (info: { tier: 'pro' | 'premium' | 'enterprise'; feature: string; description: string }) => void;
  autoOpenCamera?: boolean;
  autoStartVoice?: boolean;
}

type QuickCategory = 'all' | 'costs' | 'crm' | 'agenda' | 'stock' | 'cashier' | 'nav';

export const AgenticCommandModal: React.FC<AgenticCommandModalProps> = ({
  isOpen,
  onClose,
  userTier = 'empresa',
  onRefreshData,
  onNavigate,
  costsViewId = BRIDGE_DESTINATIONS.costs.default,
  financialViewId = BRIDGE_DESTINATIONS.financial.default,
  clientsViewId = BRIDGE_DESTINATIONS.clients.default,
  inventoryViewId = BRIDGE_DESTINATIONS.inventory.default,
  appointmentsViewId = BRIDGE_DESTINATIONS.appointments.default,
  workOrdersViewId = BRIDGE_DESTINATIONS.work_orders.default,
  onRequestUpgrade,
  autoOpenCamera = false,
  autoStartVoice = false
}) => {
  const [prompt, setPrompt] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [response, setResponse] = useState<AgenticCommandResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<QuickCategory>('all');
  const [isListening, setIsListening] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);

  const toggleVoiceInput = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Seu navegador não suporta reconhecimento de voz. Tente digitar o comando ou enviar foto da nota.');
      return;
    }

    if (isListening && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      setIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'pt-BR';
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results?.[0]?.[0]?.transcript;
        if (transcript) {
          setPrompt((prev) => (prev ? `${prev} ${transcript}` : transcript));
        }
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch {
      setIsListening(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 50);
      setErrorMsg(null);
      setSuccessToast(null);

      if (autoOpenCamera) {
        setTimeout(() => cameraInputRef.current?.click(), 120);
      } else if (autoStartVoice) {
        setTimeout(() => toggleVoiceInput(), 150);
      }
    } else {
      if (isListening && recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
      }
      setIsListening(false);
      setPrompt('');
      handleRemoveAttachment();
      setResponse(null);
      setErrorMsg(null);
      setSuccessToast(null);
      setStatusText(null);
    }
  }, [isOpen, autoOpenCamera, autoStartVoice]);

  // Listener para atalho Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Ajuste automático de altura da textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [prompt]);

  const handleSelectFile = (file: File) => {
    setAttachedFile(file);
    setErrorMsg(null);
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
    } else {
      setFilePreviewUrl(null);
    }
  };

  const handleRemoveAttachment = () => {
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }
    setAttachedFile(null);
    setFilePreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if ((!prompt.trim() && !attachedFile) || loading || confirming) return;

    setLoading(true);
    setErrorMsg(null);
    setSuccessToast(null);
    setResponse(null);

    try {
      // Caso 1: Anexo de Documento Fiscal (PDF ou Foto de Cupom)
      if (attachedFile) {
        let docRes: AgenticCommandResponse | null = null;
        if (looksLikeFinancialIntent(prompt)) {
          setStatusText('Lendo documento e preparando título financeiro...');
          docRes = await prepareFinancialDraft(prompt.trim(), attachedFile);
        }
        if (!docRes && looksLikeServicesIntent(prompt)) {
          setStatusText('Lendo documento e identificando serviços...');
          docRes = await prepareServicesDraft(prompt.trim(), attachedFile);
        }
        if (!docRes && looksLikeClientIntent(prompt)) {
          setStatusText('Lendo documento/cartão e identificando cliente...');
          docRes = await prepareClientDraft(prompt.trim(), attachedFile);
        }
        if (!docRes && looksLikeInventoryIntent(prompt)) {
          setStatusText('Lendo documento e identificando lote/estoque...');
          docRes = await prepareInventoryDraft(prompt.trim(), attachedFile);
        }
        if (!docRes && looksLikeAppointmentIntent(prompt)) {
          setStatusText('Lendo documento e identificando agendamento...');
          docRes = await prepareAppointmentDraft(prompt.trim(), attachedFile);
        }
        if (!docRes && looksLikeWorkOrderIntent(prompt)) {
          setStatusText('Lendo documento e preparando ordem de serviço...');
          docRes = await prepareWorkOrderDraft(prompt.trim(), attachedFile);
        }
        if (!docRes && looksLikeRecipeIntent(prompt)) {
          setStatusText('Lendo documento e identificando ficha técnica...');
          docRes = await prepareRecipeDraft(prompt.trim(), attachedFile);
        }
        if (!docRes) {
          setStatusText('Lendo documento fiscal e extraindo insumos...');
          docRes = await processAgenticDocumentFile(attachedFile);
        }
        setResponse(docRes);
        handleRemoveAttachment();
      } else {
        // Caso 2: Comando em Linguagem Natural
        setStatusText('Interpretando comando operacional...');
        const res = await sendAgenticCommand(prompt.trim());
        setResponse(res);

        // Se for comando de navegação direta
        if (res.type === 'navigation' && res.navigationTarget?.view && onNavigate) {
          setSuccessToast(`Redirecionando para ${res.navigationTarget.label}...`);
          setTimeout(() => {
            onNavigate(res.navigationTarget!.view);
            onClose();
          }, 400);
        }
      }
    } catch (err: any) {
      let rawMsg = err.message || 'Falha ao processar comando ou documento.';
      if (rawMsg.includes('{"error"') || rawMsg.includes('ACCESS_TOKEN') || rawMsg.includes('Erro no Gemini')) {
        rawMsg = 'Serviço de IA temporariamente indisponível. Tente novamente em alguns instantes.';
      }
      setErrorMsg(rawMsg);
    } finally {
      setLoading(false);
      setStatusText(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  /**
   * Ponte Omnibar -> Centro de Custos: grava os itens lidos na fila de validação
   * (mesma chave usada por useCostCenterAI) e abre a tela de Materiais.
   */
  const handleSendToCostCenter = async () => {
    const items = response?.proposal?.items;
    if (!items || items.length === 0) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        setErrorMsg('Sessão de usuário não identificada. Entre novamente para continuar.');
        return;
      }

      const storageKey = getBridgeStorageKey('costs', userId);
      let existing: any[] = [];
      try {
        existing = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
        if (!Array.isArray(existing)) existing = [];
      } catch {
        existing = [];
      }

      const staged = items.map((it) => ({
        id: crypto.randomUUID(),
        name: it.name,
        category: it.category === 'fixo' ? 'fixo' : 'variável',
        unit: it.unit || 'un',
        price: Number(it.price || 0),
        quantity: Number(it.quantity || 1),
        isAsset: it.category === 'fixo'
      }));

      sessionStorage.setItem(storageKey, JSON.stringify([...existing, ...staged]));
      window.dispatchEvent(new CustomEvent(BRIDGE_EVENTS.costs));

      setResponse(null);
      onNavigate?.(costsViewId);
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Não foi possível enviar os itens para o Centro de Custos.');
    }
  };

  /**
   * Ponte Omnibar -> Contas a Pagar & Receber (plano Empresa): entrega o rascunho
   * para a tela oficial, que valida e grava. Nada é gravado aqui.
   */
  const handleOpenFinancialDraft = async () => {
    const draft = response?.financialDraft;
    if (!draft) return;

    if (userTier !== 'empresa') {
      setResponse(null);
      onClose();
      onRequestUpgrade?.({
        tier: 'enterprise',
        feature: 'Contas a Pagar e Receber',
        description: 'Controle de vencimentos, baixa de títulos e fluxo de caixa no plano Empresa.'
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        setErrorMsg('Sessão de usuário não identificada. Entre novamente para continuar.');
        return;
      }
      stageFinancialDraft(userId, draft);
      setResponse(null);
      onNavigate?.(financialViewId);
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Não foi possível abrir Contas a Pagar e Receber.');
    }
  };

  /**
   * Ponte Omnibar -> Catálogo de Serviços (Centro de Custos, aba Serviços):
   * entrega os serviços lidos para a fila de validação. Nada é gravado aqui.
   */
  const handleOpenServicesDraft = async () => {
    const items = response?.servicesDraft;
    if (!items || items.length === 0) return;

    if (userTier === 'free') {
      setResponse(null);
      onClose();
      onRequestUpgrade?.({
        tier: 'pro',
        feature: 'Catálogo de Serviços',
        description: 'Cadastro de serviços com valores e tipo de cobrança no plano Pro.'
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        setErrorMsg('Sessão de usuário não identificada. Entre novamente para continuar.');
        return;
      }
      stageServiceDrafts(userId, items);
      setResponse(null);
      onNavigate?.(costsViewId);
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Não foi possível enviar os serviços para o catálogo.');
    }
  };

  /**
   * Ponte Omnibar -> CRM de Clientes (plano Especialista/Empresa)
   */
  const handleOpenClientDraft = async () => {
    const draft = response?.clientDraft;
    if (!draft) return;

    if (userTier === 'free' || userTier === 'pro') {
      setResponse(null);
      onClose();
      onRequestUpgrade?.({
        tier: 'premium',
        feature: 'CRM de Clientes',
        description: 'Gestão de contatos, clientes e histórico de atendimento no plano Especialista.'
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        setErrorMsg('Sessão de usuário não identificada. Entre novamente para continuar.');
        return;
      }
      stageClientDraft(userId, draft);
      setResponse(null);
      onNavigate?.(clientsViewId);
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Não foi possível abrir o CRM de Clientes.');
    }
  };

  /**
   * Ponte Omnibar -> Estoque & Lotes (plano Empresa)
   */
  const handleOpenInventoryDraft = async () => {
    const draft = response?.inventoryDraft;
    if (!draft) return;

    if (userTier !== 'empresa') {
      setResponse(null);
      onClose();
      onRequestUpgrade?.({
        tier: 'enterprise',
        feature: 'Gestão de Estoque e Lotes',
        description: 'Controle de validade, balancete financeiro e extrato de lotes no plano Empresa.'
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        setErrorMsg('Sessão de usuário não identificada. Entre novamente para continuar.');
        return;
      }
      stageInventoryDraft(userId, draft);
      setResponse(null);
      onNavigate?.(inventoryViewId);
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Não foi possível abrir a tela de Estoque.');
    }
  };

  /**
   * Ponte Omnibar -> Agenda (plano Especialista/Empresa)
   */
  const handleOpenAppointmentDraft = async () => {
    const draft = response?.appointmentDraft;
    if (!draft) return;

    if (userTier === 'free' || userTier === 'pro') {
      setResponse(null);
      onClose();
      onRequestUpgrade?.({
        tier: 'premium',
        feature: 'Agenda Multi-Atendentes',
        description: 'Gestão de horários, profissionais e agendamentos no plano Especialista.'
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        setErrorMsg('Sessão de usuário não identificada. Entre novamente para continuar.');
        return;
      }
      stageAppointmentDraft(userId, draft);
      setResponse(null);
      onNavigate?.(appointmentsViewId);
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Não foi possível abrir a Agenda.');
    }
  };

  /**
   * Ponte Omnibar -> Ordens de Serviço (plano Especialista/Empresa)
   */
  const handleOpenWorkOrderDraft = async () => {
    const draft = response?.workOrderDraft;
    if (!draft) return;

    if (userTier === 'free' || userTier === 'pro') {
      setResponse(null);
      onClose();
      onRequestUpgrade?.({
        tier: 'premium',
        feature: 'Ordens de Serviço',
        description: 'Emissão de OS com fotos, checklist técnico e assinatura digital no plano Especialista.'
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        setErrorMsg('Sessão de usuário não identificada. Entre novamente para continuar.');
        return;
      }
      stageWorkOrderDraft(userId, draft);
      setResponse(null);
      onNavigate?.(workOrdersViewId);
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Não foi possível abrir Ordens de Serviço.');
    }
  };

  /**
   * Ponte Omnibar -> Fichas Técnicas e Receitas (plano Pro+)
   */
  const handleOpenRecipeDraft = async () => {
    const draft = response?.recipeDraft;
    if (!draft) return;

    if (userTier === 'free') {
      setResponse(null);
      onClose();
      onRequestUpgrade?.({
        tier: 'pro',
        feature: 'Fichas Técnicas e Receitas',
        description: 'Cálculo de custo de produção, rendimento e ingredientes no plano Pro.'
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        setErrorMsg('Sessão de usuário não identificada. Entre novamente para continuar.');
        return;
      }
      stageRecipeDraft(userId, draft);
      setResponse(null);
      onNavigate?.(costsViewId);
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Não foi possível enviar a receita para o Centro de Custos.');
    }
  };

  const handleConfirmAction = async () => {
    if (!response?.proposal) return;

    setConfirming(true);
    setErrorMsg(null);

    try {
      const res = await confirmAgenticMutation(
        response.proposal.toolName,
        response.proposal.parameters
      );

      if (res.success) {
        setSuccessToast(res.message || 'Lançamento efetuado com sucesso!');
        setResponse(null);
        setPrompt('');
        if (onRefreshData) onRefreshData();
        try {
          window.dispatchEvent(new CustomEvent('rmax:data-updated', {
            detail: { toolName: response.proposal.toolName }
          }));
        } catch {}
      } else {
        setErrorMsg(res.message || 'Não foi possível gravar o lançamento.');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Falha ao confirmar ação.');
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  const quickCategories = [
    { id: 'all' as QuickCategory, label: 'Todos', icon: Zap },
    { id: 'costs' as QuickCategory, label: 'Custos & NFs', icon: Tag },
    { id: 'crm' as QuickCategory, label: 'Clientes', icon: Users },
    { id: 'agenda' as QuickCategory, label: 'Agenda', icon: Calendar },
    { id: 'stock' as QuickCategory, label: 'Estoque', icon: Package },
    { id: 'cashier' as QuickCategory, label: 'Caixa & Vendas', icon: DollarSign },
    { id: 'nav' as QuickCategory, label: 'Telas', icon: Compass },
  ];

  const categoryPrompts: Record<QuickCategory, string[]> = {
    all: [
      'Comprei 10kg de queijo por R$ 380',
      'Sobe o custo da hora para R$ 85',
      'Cadastrar cliente Maria tel 11 98888-0000',
      'Consultar agendamentos de hoje',
      'Saldo de estoque de insumos',
      'Fechamento do caixa de hoje'
    ],
    costs: [
      'Comprei 10kg de queijo por R$ 380',
      'Paguei R$ 150 de conta de luz',
      'Gastei 50 reais em embalagens',
      'Cadastrar despesa fixa de internet R$ 120',
      'Sobe o custo da hora para R$ 85',
      'Consultar despesas operacionais fixas'
    ],
    crm: [
      'Cadastrar cliente Carlos tel 11 99999-8888',
      'Buscar cliente João',
      'Consultar histórico da cliente Mariana'
    ],
    agenda: [
      'Consultar agendamentos de hoje',
      'Agendar corte amanhã às 14h com Marcos',
      'Verificar horários vagos hoje'
    ],
    stock: [
      'Saldo de estoque de insumos',
      'Lotes com validade nos próximos 30 dias',
      'Consultar balancete de estoque'
    ],
    cashier: [
      'Fechamento do caixa de hoje',
      'Resumo de vendas deste mês',
      'Últimos 5 orçamentos emitidos'
    ],
    nav: [
      'Ir para Sorveteria / PDV',
      'Abrir Radar de Mercado',
      'Abrir Centro de Custos',
      'Ir para Agenda',
      'Ir para Clientes'
    ]
  };

  const activePrompts = categoryPrompts[selectedCategory] || categoryPrompts.all;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 sm:pt-20 px-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div 
        className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
      >
        {/* ========================================================================= */}
        {/* CONTAINER DA BARRA DE ENTRADA ESTILO IA (CHATGPT/GEMINI COM ANEXOS À ESQ) */}
        {/* ========================================================================= */}
        <div className="p-3 sm:p-4 border-b border-slate-200 bg-slate-50/70">
          
          {/* Chip de Anexo Ativo (quando um arquivo for anexado) */}
          {attachedFile && (
            <div className="mb-2.5 flex items-center justify-between p-2 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <div className="flex items-center gap-2.5 min-w-0">
                {filePreviewUrl ? (
                  <img src={filePreviewUrl} alt="Preview" className="w-9 h-9 object-cover rounded-lg border border-amber-300" />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-amber-700" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-semibold truncate max-w-[280px] sm:max-w-md">{attachedFile.name}</div>
                  <div className="text-[10px] text-amber-700">
                    {(attachedFile.size / 1024).toFixed(1)} KB • Pronto para leitura fiscal
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveAttachment}
                className="p-1 rounded-lg hover:bg-amber-200/50 text-amber-800 transition-colors"
                title="Remover anexo"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Banner de Gravação por Voz Ativa */}
          {isListening && (
            <div className="mb-2.5 flex items-center justify-between p-2.5 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-800 animate-in fade-in duration-150">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-600"></span>
                </span>
                <span className="font-bold">Ouvindo você... pode falar seu comando</span>
              </div>
              <button
                type="button"
                onClick={toggleVoiceInput}
                className="text-[11px] font-bold text-rose-700 bg-white border border-rose-200 px-2.5 py-1 rounded-lg hover:bg-rose-100 transition-colors cursor-pointer"
              >
                Concluir fala
              </button>
            </div>
          )}

          {/* Caixa de Entrada Integrada com Botões de Anexo à Esquerda */}
          <div className="relative flex items-center gap-2 bg-white rounded-xl border border-slate-300 px-3 py-2 shadow-xs focus-within:ring-2 focus-within:ring-amber-500/20 focus-within:border-amber-500 transition-all">
            
            {/* LADO ESQUERDO: Botões de Anexo (📎 Arquivo / 📷 Câmera) */}
            <div className="flex items-center gap-1 shrink-0 border-r border-slate-200 pr-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleSelectFile(f);
                }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleSelectFile(f);
                }}
              />

              {/* Botão de Anexo Geral (Nota Fiscal PDF ou Foto de Cupom) */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || confirming}
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
                title="Anexar Nota Fiscal (PDF) ou Foto de Cupom"
              >
                <Paperclip className="w-4 h-4" />
              </button>

              {/* Botão de Câmera Rápida (para celular ou tablet) */}
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={loading || confirming}
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
                title="Fotografar Cupom Fiscal com a Câmera"
              >
                <Camera className="w-4 h-4" />
              </button>

              {/* Botão de Voz / Microfone */}
              <button
                type="button"
                onClick={toggleVoiceInput}
                disabled={loading || confirming}
                className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                  isListening
                    ? 'bg-rose-100 text-rose-700 animate-pulse border border-rose-300'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                }`}
                title={isListening ? 'Ouvindo... toque para parar' : 'Falar comando por voz'}
              >
                {isListening ? <MicOff className="w-4 h-4 text-rose-600" /> : <Mic className="w-4 h-4" />}
              </button>
            </div>

            {/* CENTRO: Textarea Multilinha com placeholder descritivo */}
            <textarea
              ref={textareaRef}
              rows={1}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                attachedFile 
                  ? "Pressione Executar ou adicione uma observação sobre esta nota fiscal..." 
                  : isListening
                  ? "Ouvindo você falar... pode dizer o comando!"
                  : "No que posso te ajudar hoje? (ex: 'Comprei 10kg de queijo por R$ 380' ou anexe a nota)"
              }
              className="w-full text-sm sm:text-base bg-transparent text-slate-900 placeholder-slate-400 focus:outline-none resize-none min-h-[36px] max-h-[120px] py-1.5 leading-relaxed"
              disabled={loading || confirming}
            />

            {/* LADO DIREITO: Botão Executar e Fechar */}
            <div className="flex items-center gap-1 shrink-0 pl-1">
              {loading ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-700 font-medium">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                  <span className="hidden sm:inline">Lendo...</span>
                </div>
              ) : (prompt.trim() || attachedFile) ? (
                <button
                  type="button"
                  onClick={() => handleSubmit()}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1 shadow-xs transition-all active:scale-95 cursor-pointer"
                  title="Executar comando (Enter)"
                >
                  <Zap className="w-3.5 h-3.5 fill-current" />
                  <span className="hidden sm:inline">Executar</span>
                  <CornerDownLeft className="w-3 h-3 opacity-75" />
                </button>
              ) : null}

              <button
                type="button"
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                title="Fechar (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Mensagem de Progresso Operacional */}
          {statusText && (
            <div className="mt-2 text-xs text-amber-700 font-medium flex items-center gap-1.5 animate-pulse px-1">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>{statusText}</span>
            </div>
          )}
        </div>

        {/* ========================================================================= */}
        {/* CORPO DO MODAL (RESPOSTAS, CARDS DE PROPOSTA E CONSULTAS) */}
        {/* ========================================================================= */}
        <div className="max-h-[62vh] overflow-y-auto p-4 sm:p-5 space-y-4">
          
          {/* Mensagens de Erro */}
          {errorMsg && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>{errorMsg}</div>
            </div>
          )}

          {/* Mensagens de Sucesso */}
          {successToast && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-start gap-2.5">
              <Check className="w-4 h-4 mt-0.5 shrink-0" />
              <div>{successToast}</div>
            </div>
          )}

          {/* CASO 1: Ponte para a Esteira de Validação do Centro de Custos (Nota Fiscal / Cupom) */}
          {response?.type === 'proposal' && response.proposal?.items && response.proposal.items.length > 0 && (
            <div className="border border-slate-300 bg-white rounded-xl p-4 sm:p-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-slate-900 text-base font-bold">
                  <ShieldCheck className="w-5 h-5 text-amber-700" />
                  <span>{response.proposal.items.length} item(ns) identificado(s) no documento</span>
                </div>
                {response.proposal.totalValue !== undefined && (
                  <div className="text-sm font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded-lg border border-slate-300">
                    Total: R$ {response.proposal.totalValue.toFixed(2)}
                  </div>
                )}
              </div>

              <p className="text-sm text-slate-700 leading-relaxed">
                Os itens serão enviados para a tela de validação do Centro de Custos (Materiais), onde você confere nome, unidade, categoria e preço de cada insumo antes de gravar.
              </p>

              {/* Lista legível dos itens identificados */}
              <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg bg-white max-h-64 overflow-y-auto">
                {response.proposal.items.map((it, idx) => (
                  <li key={idx} className="px-3 py-2.5 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 break-words">{it.name}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{it.quantity || 1} {it.unit}</div>
                    </div>
                    <div className="text-sm font-bold text-slate-900 whitespace-nowrap">R$ {Number(it.price || 0).toFixed(2)}</div>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setResponse(null)}
                  className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSendToCostCenter}
                  className="px-4 py-3 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 cursor-pointer"
                >
                  <span>Revisar no Centro de Custos</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* CASO 1B: Rascunho de título financeiro (Contas a Pagar / Receber) */}
          {response?.type === 'financial_draft' && response.financialDraft && (
            <div className="border border-slate-300 bg-white rounded-xl p-4 sm:p-5 space-y-4">
              <div className="flex items-center gap-2 text-slate-900 text-base font-bold">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
                <span>{response.financialDraft.tipo === 'RECEITA' ? 'Conta a receber' : 'Conta a pagar'}: rascunho</span>
              </div>

              <dl className="text-sm divide-y divide-slate-200 border border-slate-200 rounded-lg">
                {[
                  ['Descrição', response.financialDraft.descricao],
                  ['Valor', response.financialDraft.valorOriginal > 0 ? `R$ ${response.financialDraft.valorOriginal.toFixed(2)}` : 'Não informado'],
                  ['Vencimento', response.financialDraft.dataVencimento ? response.financialDraft.dataVencimento.split('-').reverse().join('/') : 'Não informado'],
                  [response.financialDraft.tipo === 'RECEITA' ? 'Cliente' : 'Fornecedor', response.financialDraft.pessoaNome || 'Não informado'],
                  ...(response.financialDraft.numeroDocumento ? [['Documento', response.financialDraft.numeroDocumento]] : [])
                ].map(([label, value]) => (
                  <div key={label} className="px-3 py-2.5 flex items-start justify-between gap-3">
                    <dt className="text-slate-600">{label}</dt>
                    <dd className="font-semibold text-slate-900 text-right break-words">{value}</dd>
                  </div>
                ))}
              </dl>

              <p className="text-sm text-slate-700 leading-relaxed">
                {userTier === 'empresa'
                  ? 'Os dados serão levados para Contas a Pagar e Receber. Você confere, completa o que faltar e grava por lá.'
                  : 'Contas a Pagar e Receber faz parte do plano Empresa. Ao continuar, você verá as condições do plano.'}
              </p>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setResponse(null)}
                  className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleOpenFinancialDraft}
                  className="px-4 py-3 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 cursor-pointer"
                >
                  <span>{userTier === 'empresa' ? 'Revisar em Contas a Pagar e Receber' : 'Ver plano Empresa'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* CASO 1C: Serviços identificados (Catálogo de Serviços) */}
          {response?.type === 'services_draft' && response.servicesDraft && response.servicesDraft.length > 0 && (
            <div className="border border-slate-300 bg-white rounded-xl p-4 sm:p-5 space-y-4">
              <div className="flex items-center gap-2 text-slate-900 text-base font-bold">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
                <span>{response.servicesDraft.length} serviço(s) identificado(s)</span>
              </div>

              <p className="text-sm text-slate-700 leading-relaxed">
                Os serviços serão levados para o Catálogo de Serviços, onde você confere nome, tipo de cobrança e valor antes de gravar.
              </p>

              <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg bg-white max-h-64 overflow-y-auto">
                {response.servicesDraft.map((s, idx) => (
                  <li key={idx} className="px-3 py-2.5 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 break-words">{s.name}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{s.type === 'hourly' ? 'Cobrança por hora' : 'Valor fechado'}</div>
                    </div>
                    <div className="text-sm font-bold text-slate-900 whitespace-nowrap">
                      {s.value > 0 ? `R$ ${s.value.toFixed(2)}` : 'Valor a definir'}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setResponse(null)}
                  className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleOpenServicesDraft}
                  className="px-4 py-3 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 cursor-pointer"
                >
                  <span>{userTier === 'free' ? 'Ver plano Pro' : 'Revisar no Catálogo de Serviços'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* CASO 1D: Cliente identificado (CRM de Clientes) */}
          {response?.type === 'client_draft' && response.clientDraft && (
            <div className="border border-slate-300 bg-white rounded-xl p-4 sm:p-5 space-y-4">
              <div className="flex items-center gap-2 text-slate-900 text-base font-bold">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
                <span>Cliente: rascunho</span>
              </div>

              <dl className="text-sm divide-y divide-slate-200 border border-slate-200 rounded-lg">
                {[
                  ['Nome / Razão Social', response.clientDraft.name],
                  ...(response.clientDraft.phone ? [['Telefone / WhatsApp', response.clientDraft.phone]] : []),
                  ...(response.clientDraft.email ? [['E-mail', response.clientDraft.email]] : []),
                  ...(response.clientDraft.document ? [['CPF / CNPJ', response.clientDraft.document]] : []),
                  ...(response.clientDraft.address ? [['Endereço', response.clientDraft.address]] : []),
                  ...(response.clientDraft.notes ? [['Observações', response.clientDraft.notes]] : [])
                ].map(([label, value]) => (
                  <div key={label} className="px-3 py-2.5 flex items-start justify-between gap-3">
                    <dt className="text-slate-600">{label}</dt>
                    <dd className="font-semibold text-slate-900 text-right break-words">{value}</dd>
                  </div>
                ))}
              </dl>

              <p className="text-sm text-slate-700 leading-relaxed">
                {userTier === 'especialista' || userTier === 'empresa'
                  ? 'Os dados serão levados para o CRM de Clientes. Você confere, completa o que faltar e salva por lá.'
                  : 'O CRM de Clientes faz parte do plano Especialista. Ao continuar, você verá as condições do plano.'}
              </p>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setResponse(null)}
                  className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleOpenClientDraft}
                  className="px-4 py-3 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 cursor-pointer"
                >
                  <span>{userTier === 'free' || userTier === 'pro' ? 'Ver plano Especialista' : 'Revisar no CRM de Clientes'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* CASO 1E: Entrada de Estoque / Lote com Validade */}
          {response?.type === 'inventory_draft' && response.inventoryDraft && (
            <div className="border border-slate-300 bg-white rounded-xl p-4 sm:p-5 space-y-4">
              <div className="flex items-center gap-2 text-slate-900 text-base font-bold">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
                <span>Entrada de Estoque / Lote: rascunho</span>
              </div>

              <dl className="text-sm divide-y divide-slate-200 border border-slate-200 rounded-lg">
                {[
                  ['Item / Material', response.inventoryDraft.materialName],
                  ...(response.inventoryDraft.supplierName ? [['Fornecedor', response.inventoryDraft.supplierName]] : []),
                  ...(response.inventoryDraft.barcode ? [['Cód. Barras / QR', response.inventoryDraft.barcode]] : []),
                  ...(response.inventoryDraft.stockControlMode ? [['Controle de Estoque', response.inventoryDraft.stockControlMode === 'pot_open' ? 'Pote Aberto / Cubeta' : 'Venda Unitária / Unidade']] : []),
                  ['Tipo de Movimento', (() => {
                    switch (response.inventoryDraft.type) {
                      case 'exit_pot_open': return 'Baixa: Abertura de Pote (Buffet)';
                      case 'exit_freezer_restock': return 'Baixa: Reposição de Freezer';
                      case 'exit_loss_damaged': return 'Baixa: Avaria / Descarte';
                      case 'exit_loss_expired': return 'Baixa: Produto Vencido';
                      case 'exit_internal': return 'Baixa: Consumo Interno';
                      case 'entry_adjustment': return 'Ajuste de Estoque (+)';
                      default: return 'Compra / Entrada de Estoque';
                    }
                  })()],
                  ['Quantidade', String(response.inventoryDraft.quantity)],
                  ...(response.inventoryDraft.unitCost ? [['Custo Unitário', `R$ ${response.inventoryDraft.unitCost.toFixed(2)}`]] : []),
                  ...(response.inventoryDraft.batchCode ? [['Código do Lote', response.inventoryDraft.batchCode]] : []),
                  ...(response.inventoryDraft.expirationDate ? [['Data de Validade', response.inventoryDraft.expirationDate.split('-').reverse().join('/')]] : []),
                  ...(response.inventoryDraft.notes ? [['Observações', response.inventoryDraft.notes]] : [])
                ].map(([label, value]) => (
                  <div key={label} className="px-3 py-2.5 flex items-start justify-between gap-3">
                    <dt className="text-slate-600">{label}</dt>
                    <dd className="font-semibold text-slate-900 text-right break-words">{value}</dd>
                  </div>
                ))}
              </dl>

              <p className="text-sm text-slate-700 leading-relaxed">
                {userTier === 'empresa'
                  ? 'Os dados serão levados para a Gestão de Estoque e Lotes. Você confere e confirma o lançamento por lá.'
                  : 'A Gestão de Estoque e Lotes faz parte do plano Empresa. Ao continuar, você verá as condições do plano.'}
              </p>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setResponse(null)}
                  className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleOpenInventoryDraft}
                  className="px-4 py-3 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 cursor-pointer"
                >
                  <span>{userTier !== 'empresa' ? 'Ver plano Empresa' : 'Revisar no Estoque'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* CASO 1F: Agendamento de Horário */}
          {response?.type === 'appointment_draft' && response.appointmentDraft && (
            <div className="border border-slate-300 bg-white rounded-xl p-4 sm:p-5 space-y-4">
              <div className="flex items-center gap-2 text-slate-900 text-base font-bold">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
                <span>Agendamento: rascunho</span>
              </div>

              <dl className="text-sm divide-y divide-slate-200 border border-slate-200 rounded-lg">
                {[
                  ['Cliente', response.appointmentDraft.clientName],
                  ['Serviço / Atendimento', response.appointmentDraft.serviceName],
                  ['Data Prevista', response.appointmentDraft.scheduledDate ? response.appointmentDraft.scheduledDate.split('-').reverse().join('/') : 'Hoje'],
                  ['Horário', response.appointmentDraft.scheduledTime || 'A definir'],
                  ...(response.appointmentDraft.clientPhone ? [['Telefone', response.appointmentDraft.clientPhone]] : []),
                  ...(response.appointmentDraft.price ? [['Valor', `R$ ${response.appointmentDraft.price.toFixed(2)}`]] : []),
                  ...(response.appointmentDraft.durationMinutes ? [['Duração', `${response.appointmentDraft.durationMinutes} minutos`]] : []),
                  ...(response.appointmentDraft.notes ? [['Observações', response.appointmentDraft.notes]] : [])
                ].map(([label, value]) => (
                  <div key={label} className="px-3 py-2.5 flex items-start justify-between gap-3">
                    <dt className="text-slate-600">{label}</dt>
                    <dd className="font-semibold text-slate-900 text-right break-words">{value}</dd>
                  </div>
                ))}
              </dl>

              <p className="text-sm text-slate-700 leading-relaxed">
                {userTier === 'especialista' || userTier === 'empresa'
                  ? 'Os dados serão levados para a Agenda. Você escolhe o profissional e confirma o horário por lá.'
                  : 'A Agenda Multi-Atendentes faz parte do plano Especialista. Ao continuar, você verá as condições do plano.'}
              </p>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setResponse(null)}
                  className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleOpenAppointmentDraft}
                  className="px-4 py-3 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 cursor-pointer"
                >
                  <span>{userTier === 'free' || userTier === 'pro' ? 'Ver plano Especialista' : 'Revisar na Agenda'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* CASO 1G: Ordem de Serviço (OS) */}
          {response?.type === 'work_order_draft' && response.workOrderDraft && (
            <div className="border border-slate-300 bg-white rounded-xl p-4 sm:p-5 space-y-4">
              <div className="flex items-center gap-2 text-slate-900 text-base font-bold">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
                <span>Ordem de Serviço: rascunho</span>
              </div>

              <dl className="text-sm divide-y divide-slate-200 border border-slate-200 rounded-lg">
                {[
                  ['Título da OS', response.workOrderDraft.title],
                  ...(response.workOrderDraft.clientName ? [['Cliente', response.workOrderDraft.clientName]] : []),
                  ...(response.workOrderDraft.deviceInfo ? [['Aparelho / Equipamento', response.workOrderDraft.deviceInfo]] : []),
                  ...(response.workOrderDraft.problemDescription ? [['Defeito / Problema', response.workOrderDraft.problemDescription]] : []),
                  ...(response.workOrderDraft.clientPhone ? [['Telefone', response.workOrderDraft.clientPhone]] : []),
                  ...(response.workOrderDraft.notes ? [['Observações', response.workOrderDraft.notes]] : [])
                ].map(([label, value]) => (
                  <div key={label} className="px-3 py-2.5 flex items-start justify-between gap-3">
                    <dt className="text-slate-600">{label}</dt>
                    <dd className="font-semibold text-slate-900 text-right break-words">{value}</dd>
                  </div>
                ))}
              </dl>

              <p className="text-sm text-slate-700 leading-relaxed">
                {userTier === 'especialista' || userTier === 'empresa'
                  ? 'Os dados serão levados para a Ordem de Serviço. Você anexa fotos, realiza a vistoria e assina por lá.'
                  : 'A emissão de Ordens de Serviço faz parte do plano Especialista. Ao continuar, você verá as condições do plano.'}
              </p>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setResponse(null)}
                  className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleOpenWorkOrderDraft}
                  className="px-4 py-3 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 cursor-pointer"
                >
                  <span>{userTier === 'free' || userTier === 'pro' ? 'Ver plano Especialista' : 'Revisar Ordem de Serviço'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* CASO 1H: Ficha Técnica / Receita */}
          {response?.type === 'recipe_draft' && response.recipeDraft && (
            <div className="border border-slate-300 bg-white rounded-xl p-4 sm:p-5 space-y-4">
              <div className="flex items-center gap-2 text-slate-900 text-base font-bold">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
                <span>Ficha Técnica: rascunho</span>
              </div>

              <div className="space-y-3">
                <div className="text-sm font-bold text-slate-900">{response.recipeDraft.name}</div>
                <div className="text-xs text-slate-600 flex gap-4">
                  <span>Rendimento: {response.recipeDraft.yieldAmount} {response.recipeDraft.yieldUnit || 'un'}</span>
                  {response.recipeDraft.preparationTimeMinutes ? <span>Preparo: {response.recipeDraft.preparationTimeMinutes} min</span> : null}
                </div>

                <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg bg-white max-h-56 overflow-y-auto">
                  {response.recipeDraft.items.map((it, idx) => (
                    <li key={idx} className="px-3 py-2 flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-900">{it.name}</span>
                      <span className="text-slate-600 font-semibold">{it.quantity} {it.unit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-sm text-slate-700 leading-relaxed">
                {userTier !== 'free'
                  ? 'Os dados serão levados para o Centro de Custos (Receitas), onde você confere cada ingrediente e calcula a margem.'
                  : 'Fichas Técnicas e Receitas fazem parte do plano Pro. Ao continuar, você verá as condições do plano.'}
              </p>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setResponse(null)}
                  className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleOpenRecipeDraft}
                  className="px-4 py-3 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 cursor-pointer"
                >
                  <span>{userTier === 'free' ? 'Ver plano Pro' : 'Revisar no Centro de Custos'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* CASO 2: Proposta Padrão de Mutação (Alteração de Custo Hora, etc.) */}
          {response?.type === 'proposal' && (!response.proposal?.items || response.proposal.items.length === 0) && response.proposal && (
            <div className="border border-amber-300 bg-amber-50/50 rounded-xl p-4 sm:p-5 space-y-3 shadow-xs">
              <div className="flex items-center gap-2 text-amber-900 text-sm font-bold">
                <ShieldCheck className="w-4 h-4 text-amber-600" />
                <span>Confirmação de Ação Requerida</span>
              </div>
              <p className="text-xs text-slate-600">
                {response.proposal.description}
              </p>

              {response.proposal.diffSummary && response.proposal.diffSummary.length > 0 && (
                <div className="overflow-x-auto border border-amber-200 rounded-lg bg-white">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 font-medium">Campo</th>
                        {response.proposal.diffSummary.some(d => d.currentValue) && (
                          <th className="px-3 py-2 font-medium">Valor Atual</th>
                        )}
                        <th className="px-3 py-2 font-medium">Novo Valor Proposto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {response.proposal.diffSummary.map((diff, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2 font-medium text-slate-700">{diff.field}</td>
                          {response?.proposal?.diffSummary?.some(d => d.currentValue) && (
                            <td className="px-3 py-2 text-slate-500 line-through">{diff.currentValue || '-'}</td>
                          )}
                          <td className="px-3 py-2 font-semibold text-emerald-600">{diff.proposedValue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setResponse(null)}
                  disabled={confirming}
                  className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-200/60 rounded-lg transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAction}
                  disabled={confirming}
                  className="px-4 py-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                >
                  {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  <span>Confirmar e Gravar</span>
                </button>
              </div>
            </div>
          )}

          {/* CASO 3: Resultado de Leitura (Dados / Tabelas Estruturadas) */}
          {response?.type === 'read_result' && (
            <div className="space-y-3">
              {/* Header do Resultado com mensagem clara em destaque */}
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-2.5">
                <div className="p-1.5 rounded-lg bg-amber-100 text-amber-700 shrink-0 mt-0.5">
                  <Zap className="w-4 h-4 fill-current" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                    {response.toolName === 'custos_consultar_custo_hora' ? 'Custo da Hora Técnica' : response.toolName === 'agenda_consultar_disponibilidade' ? 'Agenda de Atendimentos' : `Consulta: ${response.toolName?.replace(/_/g, ' ')}`}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-0.5">
                    {response.message}
                  </div>
                </div>
              </div>

              {/* Card Específico para Valor da Hora Técnica */}
              {response.toolName === 'custos_consultar_custo_hora' && response.data?.valorHoraAtual && (
                <div className="p-4 rounded-xl bg-white border border-amber-200 shadow-xs space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">Valor Atual da Hora:</span>
                    <span className="text-xl font-black text-emerald-600">{response.data.valorHoraAtual}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 text-xs">
                    <div className="p-2 rounded-lg bg-slate-50">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Jornada Semanal</span>
                      <span className="font-semibold text-slate-800">{response.data.jornadaSemanal}</span>
                    </div>
                    <div className="p-2 rounded-lg bg-slate-50">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Meta Pró-labore</span>
                      <span className="font-semibold text-slate-800">{response.data.metaProLabore}</span>
                    </div>
                  </div>
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setPrompt('Sobe o custo da hora para R$ ');
                        setTimeout(() => textareaRef.current?.focus(), 50);
                      }}
                      className="text-xs font-semibold text-amber-700 hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      <span>Deseja alterar o valor da hora? Clique aqui</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Card Específico para Agenda */}
              {response.toolName === 'agenda_consultar_disponibilidade' && Array.isArray(response.data) && (
                response.data.length === 0 ? (
                  <div className="p-5 text-center rounded-xl bg-white border border-slate-200 space-y-2">
                    <Calendar className="w-8 h-8 text-slate-300 mx-auto" />
                    <div className="text-sm font-semibold text-slate-800">Nenhum compromisso marcado para este dia</div>
                    <p className="text-xs text-slate-500">Sua agenda está livre. Para marcar um novo cliente, basta falar!</p>
                    <button
                      type="button"
                      onClick={() => {
                        setPrompt('Agendar ');
                        setTimeout(() => textareaRef.current?.focus(), 50);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-800 text-xs font-semibold hover:bg-amber-100 transition-colors cursor-pointer mt-2"
                    >
                      <span>Marcar agendamento</span>
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white">
                    {response.data.map((item: any, idx: number) => (
                      <div key={idx} className="p-3 flex items-center justify-between text-xs hover:bg-slate-50">
                        <div className="flex items-center gap-2.5">
                          <span className="px-2 py-1 rounded-md bg-amber-100 text-amber-900 font-mono font-bold text-[11px]">
                            {item.scheduled_time ? String(item.scheduled_time).slice(0, 5) : 'Horário'}
                          </span>
                          <div>
                            <div className="font-semibold text-slate-900">{item.client_name}</div>
                            <div className="text-slate-500 text-[11px]">{item.service_name || 'Atendimento'}</div>
                          </div>
                        </div>
                        <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {item.status || 'Agendado'}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* Exibição genérica de tabelas ou objetos (quando não for custo hora nem agenda) */}
              {response.toolName !== 'custos_consultar_custo_hora' && response.toolName !== 'agenda_consultar_disponibilidade' && response.data && (
                Array.isArray(response.data) ? (
                  response.data.length > 0 ? (
                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-slate-100 text-slate-700">
                          <tr>
                            {Object.keys(response.data[0]).slice(0, 5).map((col) => (
                              <th key={col} className="px-3 py-2 capitalize font-medium">{col.replace(/_/g, ' ')}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 bg-white">
                          {response.data.map((row: any, i: number) => (
                            <tr key={i} className="hover:bg-slate-50">
                              {Object.keys(response.data[0]).slice(0, 5).map((col) => (
                                <td key={col} className="px-3 py-2 text-slate-700">
                                  {typeof row[col] === 'object' ? JSON.stringify(row[col]) : String(row[col] ?? '-')}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 py-3 text-center">Nenhum registro encontrado.</div>
                  )
                ) : (
                  <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-xs space-y-1 font-mono">
                    {Object.entries(response.data).map(([k, v]) => (
                      <div key={k} className="flex justify-between py-1 border-b border-slate-200 last:border-0">
                        <span className="text-slate-500">{k}:</span>
                        <span className="font-semibold text-slate-800">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {/* CASO 4: Resposta de Texto Direta */}
          {response?.type === 'text' && (
            <div className="p-3 bg-slate-50 rounded-xl text-sm text-slate-800 leading-relaxed border border-slate-200">
              {response.content}
            </div>
          )}

          {/* CASO 5: Navegação de Tela */}
          {response?.type === 'navigation' && response.navigationTarget && (
            <div className="p-4 border border-blue-200 bg-blue-50/50 rounded-xl flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-blue-900">Módulo Identificado</div>
                <div className="text-sm font-semibold text-slate-800">{response.navigationTarget.label}</div>
              </div>
              {onNavigate && (
                <button
                  type="button"
                  onClick={() => {
                    onNavigate(response.navigationTarget!.view);
                    onClose();
                  }}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <span>Abrir Tela</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}

          {/* CASO 6: Upgrade de Plano Necessário */}
          {response?.type === 'tier_required' && (
            <div className="p-4 border border-blue-200 bg-blue-50/50 rounded-xl space-y-2">
              <div className="flex items-center gap-2 text-blue-800 font-semibold text-sm">
                <Zap className="w-4 h-4 text-amber-500 fill-current" />
                <span>Recurso Exclusivo Especialista & Empresa</span>
              </div>
              <p className="text-xs text-slate-600">
                {response.message}
              </p>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SUGESTÕES DE COMANDOS ORGANIZADAS POR CATEGORIA (quando sem resposta)     */}
          {/* ========================================================================= */}
          {!response && !loading && (
            <div className="space-y-3 pt-1">
              
              {/* Filtro de Categorias (Pills) */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
                {quickCategories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = selectedCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-2.5 py-1 rounded-lg font-medium flex items-center gap-1.5 transition-colors cursor-pointer shrink-0 ${
                        isActive 
                          ? 'bg-slate-900 text-white shadow-2xs' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{cat.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Lista de Prompts da Categoria Selecionada */}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {activePrompts.map((cmd) => (
                  <button
                    key={cmd}
                    type="button"
                    onClick={() => {
                      setPrompt(cmd);
                      setTimeout(() => textareaRef.current?.focus(), 50);
                    }}
                    className="text-xs px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex items-center gap-1 cursor-pointer"
                  >
                    <span>{cmd}</span>
                    <ArrowRight className="w-3 h-3 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Rodapé Informativo */}
        <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-400 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>Anexos suportados: <strong>PDF de Nota Fiscal ou Foto de Cupom</strong></span>
          </div>
          <span>Atalho: <strong>Ctrl + K</strong></span>
        </div>
      </div>
    </div>
  );
};
